import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiCompass, FiMic, FiSend, FiVolume2, FiVolumeX, FiX } from 'react-icons/fi';
import { OPEN_AI_CHAT_EVENT, sendChatbotMessage } from '../utils/aiChatSupport';
import { speakLine, unlockAudio } from '../utils/agentVoice';
import { secureContext, speechRecognitionErrorMessage } from '../utils/mediaDevices';
import { getMenuForUserType } from '../utils/userTypeUtils';
import {
  GUIDE_WALK_EVENT,
  guideReply,
  menuTourSteps,
  pageHint,
  publicTourSteps,
  startGuide,
  startWalk,
  tutorialSteps,
} from '../utils/siteGuide';
import {
  addGuideMessage,
  getGuideSession,
  resetGuideSession,
  setGuideSession,
  subscribeGuideSession,
} from '../utils/guideSession';
import { VOICE_GENDERS, VOICE_GENDER_EVENT, readVoiceGender, saveVoiceGender } from '../utils/voicePrefs';

const VOICE_KEY = 'ew_guide_voice';
const HIDDEN_ROUTES = [/^\/interview-plans\/\d+\/rounds\//];

const Root = styled.div`
  position: fixed;
  right: 2rem;
  bottom: 2rem;
  z-index: 1300;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;
  pointer-events: none;
  > * {
    pointer-events: auto;
  }
  @media (max-width: 768px) {
    right: 1.5rem;
    bottom: 1.5rem;
  }
  @media (max-width: 480px) {
    right: 1rem;
    bottom: 1rem;
    left: 1rem;
    align-items: stretch;
  }
`;

const Panel = styled.aside`
  width: min(380px, calc(100vw - 2rem));
  height: min(480px, calc(100vh - 9rem));
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  border: 1px solid #E2E8F0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @media (max-width: 480px) {
    width: 100%;
    height: min(64vh, 480px);
  }
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 0.85rem;
  background: #0F766E;
  color: white;
`;

const Title = styled.h2`
  margin: 0;
  flex: 1;
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const IconButton = styled.button`
  background: ${(p) => (p.$on ? 'rgba(255,255,255,0.22)' : 'transparent')};
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
`;

const GenderSelect = styled.select`
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  font-size: 0.75rem;
  padding: 0.15rem 0.25rem;
  option {
    color: #0F172A;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  background: #F8FAFC;
`;

const Bubble = styled.div`
  max-width: 92%;
  padding: 0.6rem 0.8rem;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.45;
  white-space: pre-wrap;
  align-self: ${(p) => (p.$from === 'user' ? 'flex-end' : 'flex-start')};
  background: ${(p) => (p.$from === 'user' ? '#0F766E' : p.$from === 'hint' ? '#FFFBEB' : 'white')};
  color: ${(p) => (p.$from === 'user' ? 'white' : '#0F172A')};
  border: ${(p) => (p.$from === 'user' ? 'none' : p.$from === 'hint' ? '1px solid #FDE68A' : '1px solid #E2E8F0')};
`;

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.55rem 0.85rem;
  background: #F8FAFC;
  border-top: 1px solid #F1F5F9;
`;

const Chip = styled.button`
  background: white;
  border: 1px solid #99F6E4;
  color: #0F766E;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.78rem;
  cursor: pointer;
  &:hover {
    background: #F0FDFA;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Composer = styled.div`
  display: flex;
  gap: 0.45rem;
  padding: 0.65rem;
  border-top: 1px solid #E2E8F0;
  background: white;
`;

const Field = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
  font-size: 0.875rem;
  &:focus {
    outline: none;
    border-color: #0F766E;
  }
`;

const RoundButton = styled.button`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: 10px;
  background: ${(p) => (p.$live ? '#DC2626' : '#0F766E')};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Fab = styled.button`
  width: 60px;
  height: 60px;
  background: #0F766E;
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(13, 148, 136, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  &:hover {
    background: #115E59;
  }
`;

const recognitionClass = () =>
  typeof window === 'undefined' ? null : window.SpeechRecognition || window.webkitSpeechRecognition || null;

const readFlag = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value === null ? fallback : value === 'on';
  } catch {
    return fallback;
  }
};

const pointAt = (selector) => {
  let el = null;
  try {
    el = document.querySelector(selector);
  } catch {
    return;
  }
  if (!el) return;
  el.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  const before = { outline: el.style.outline, offset: el.style.outlineOffset, shadow: el.style.boxShadow };
  el.style.outline = '3px solid #F59E0B';
  el.style.outlineOffset = '2px';
  el.style.boxShadow = '0 0 0 6px rgba(245, 158, 11, 0.25)';
  setTimeout(() => {
    el.style.outline = before.outline;
    el.style.outlineOffset = before.offset;
    el.style.boxShadow = before.shadow;
  }, 3500);
};

// The engine is module-level so a tour keeps talking while layouts remount between routes.
const ctx = { navigate: null, pathname: '/', user: null, menu: [], guideNav: null };
const voice = { on: true, handsFree: false, gender: 'female', line: null, chain: Promise.resolve(), recognizer: null };

const stopVoice = () => {
  voice.line?.cancel();
  voice.line = null;
  voice.chain = Promise.resolve();
  voice.recognizer?.abort?.();
  voice.recognizer = null;
  setGuideSession({ listening: false });
};

const listen = () => {
  const Rec = recognitionClass();
  if (!Rec || voice.recognizer) return;
  const rec = new Rec();
  rec.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
  rec.interimResults = false;
  rec.maxAlternatives = 1;
  rec.onresult = (event) => {
    const said = event.results?.[0]?.[0]?.transcript || '';
    if (said.trim()) handle(said.trim()); // eslint-disable-line no-use-before-define
  };
  rec.onend = () => {
    voice.recognizer = null;
    setGuideSession({ listening: false });
  };
  rec.onerror = (event) => {
    const message = speechRecognitionErrorMessage(event?.error);
    if (message) {
      voice.handsFree = false;
      addGuideMessage('guide', message);
    }
    rec.onend();
  };
  voice.recognizer = rec;
  setGuideSession({ listening: true });
  try {
    rec.start();
  } catch {
    voice.recognizer = null;
    setGuideSession({ listening: false });
  }
};

const speak = (text, thenListen) => {
  if (!text) return;
  voice.chain = voice.chain.then(async () => {
    if (!getGuideSession().open) return;
    if (voice.on) {
      const line = speakLine({ text, useServer: false, gender: voice.gender });
      voice.line = line;
      await line.done;
      if (voice.line === line) voice.line = null;
    }
    if (thenListen && voice.handsFree && getGuideSession().open) listen();
  });
};

const apply = async (out) => {
  setGuideSession({ guide: out.state, options: out.options || [] });
  if (out.navigate && out.navigate !== ctx.pathname && ctx.navigate) {
    ctx.guideNav = out.navigate;
    ctx.navigate(out.navigate);
  }
  if (out.highlight) setTimeout(() => pointAt(out.highlight), 600);
  if (out.chat) {
    setGuideSession({ sending: true });
    try {
      const reply = await sendChatbotMessage({
        message: out.chat,
        conversationId: getGuideSession().conversationId,
        userId: ctx.user?.id,
      });
      if (reply.conversationId) setGuideSession({ conversationId: reply.conversationId });
      addGuideMessage('guide', reply.text);
      speak(reply.text, !out.say);
    } finally {
      setGuideSession({ sending: false });
    }
  }
  if (out.say) {
    addGuideMessage('guide', out.say);
    speak(out.say, true);
  }
};

const begin = () => {
  setGuideSession({ messages: [], options: [], guide: null });
  apply(startGuide({ user: ctx.user, menu: ctx.menu }));
};

function handle(text, optionId = null, label = '') {
  if (getGuideSession().sending) return;
  voice.line?.cancel();
  addGuideMessage('user', label || text);
  const current = getGuideSession().guide || startGuide({ user: ctx.user, menu: ctx.menu }).state;
  apply(guideReply(current, text, optionId));
}

const openGuide = () => setGuideSession({ open: true });

const walkFrom = (detail = {}) => {
  openGuide();
  const base = getGuideSession().guide || startGuide({ user: ctx.user, menu: ctx.menu }).state;
  if (detail.kind === 'tutorial') {
    addGuideMessage('user', `Walk me through "${detail.title || 'this guide'}"`);
    apply(startWalk(base, tutorialSteps(detail), 'tutorial', `Let's go through "${detail.title}". `));
    return;
  }
  addGuideMessage('user', 'Give me a guided tour');
  apply(startWalk(base, ctx.user ? menuTourSteps(ctx.menu) : publicTourSteps(), 'tour', "Let's start. "));
};

// Guides every visitor: welcome, user type, log in / sign up / website tour, then the menu tabs,
// guided tours and tutorial walk-throughs that pause for a yes before each next step.
const SiteGuide = ({ user = null, hidden = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const menu = useMemo(() => (user ? getMenuForUserType(user) : []), [user]);
  const session = useSyncExternalStore(subscribeGuideSession, getGuideSession);
  const [input, setInput] = useState('');
  const [voiceOn, setVoiceOn] = useState(() => readFlag(VOICE_KEY, true));
  const [handsFree, setHandsFree] = useState(false);
  const [gender, setGender] = useState(readVoiceGender);
  const bodyRef = useRef(null);

  ctx.navigate = navigate;
  ctx.pathname = location.pathname;
  ctx.user = user;
  ctx.menu = menu;
  voice.on = voiceOn;
  voice.handsFree = handsFree;
  voice.gender = gender;

  const userKey = user?.id ? String(user.id) : 'public';
  const routeHidden = HIDDEN_ROUTES.some((pattern) => pattern.test(location.pathname));
  const off = hidden || routeHidden;
  const canListen = Boolean(recognitionClass()) && secureContext();

  // Welcome once per browser session, and again ("welcome back") after signing in.
  useEffect(() => {
    if (off) return;
    if (getGuideSession().userKey !== userKey) resetGuideSession(userKey);
    const key = `ew_guide_seen_${userKey}`;
    let seen = false;
    try {
      seen = sessionStorage.getItem(key) === '1';
      sessionStorage.setItem(key, '1');
    } catch {
      /* private mode */
    }
    if (!seen || (getGuideSession().open && !getGuideSession().messages.length)) {
      openGuide();
      begin();
    }
  }, [userKey, off]);

  useEffect(() => {
    if (off) stopVoice();
  }, [off]);

  useEffect(() => {
    const onWalk = (event) => walkFrom(event.detail);
    const onOpen = () => {
      openGuide();
      if (!getGuideSession().messages.length) begin();
    };
    const onGender = (event) => setGender(event.detail === 'male' ? 'male' : 'female');
    window.addEventListener(OPEN_AI_CHAT_EVENT, onOpen);
    window.addEventListener(GUIDE_WALK_EVENT, onWalk);
    window.addEventListener(VOICE_GENDER_EVENT, onGender);
    return () => {
      window.removeEventListener(OPEN_AI_CHAT_EVENT, onOpen);
      window.removeEventListener(GUIDE_WALK_EVENT, onWalk);
      window.removeEventListener(VOICE_GENDER_EVENT, onGender);
    };
  }, []);

  // A line about each page the user opens on their own while browsing.
  useEffect(() => {
    if (ctx.guideNav === location.pathname) {
      ctx.guideNav = null;
      return;
    }
    const s = getGuideSession();
    if (off || !s.open || s.guide?.node === 'walk') return;
    const items = user ? menu : publicTourSteps().map((step) => ({ title: step.title, path: step.path }));
    const hint = pageHint(location.pathname, items);
    const last = s.messages[s.messages.length - 1];
    if (hint && last?.text !== hint) addGuideMessage('hint', hint);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      localStorage.setItem(VOICE_KEY, voiceOn ? 'on' : 'off');
    } catch {
      /* private mode */
    }
    if (!voiceOn) voice.line?.cancel();
  }, [voiceOn]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [session.messages, session.sending, session.open]);

  if (off) return null;

  const close = () => {
    stopVoice();
    setGuideSession({ open: false });
  };

  const toggle = () => {
    if (session.open) {
      close();
      return;
    }
    unlockAudio();
    openGuide();
    if (!session.messages.length) begin();
  };

  const submit = () => {
    const text = input.trim();
    if (!text) return;
    unlockAudio();
    setInput('');
    handle(text);
  };

  return (
    <Root data-testid="site-guide">
      {session.open && (
        <Panel role="complementary" aria-label="EnterWeU guide">
          <HeaderBar>
            <Title>
              <FiCompass size={16} />
              Guide
            </Title>
            <GenderSelect
              aria-label="Guide voice"
              value={gender}
              onChange={(event) => setGender(saveVoiceGender(event.target.value))}
            >
              {VOICE_GENDERS.map((choice) => (
                <option key={choice.id} value={choice.id}>{choice.id === 'male' ? 'Male' : 'Female'}</option>
              ))}
            </GenderSelect>
            <IconButton
              type="button"
              $on={voiceOn}
              onClick={() => {
                unlockAudio();
                setVoiceOn((value) => !value);
              }}
              aria-label={voiceOn ? 'Mute the guide' : 'Read replies aloud'}
              aria-pressed={voiceOn}
            >
              {voiceOn ? <FiVolume2 size={17} /> : <FiVolumeX size={17} />}
            </IconButton>
            {canListen && (
              <IconButton
                type="button"
                $on={handsFree}
                onClick={() => {
                  unlockAudio();
                  setHandsFree((value) => !value);
                }}
                aria-label="Hands-free conversation"
                aria-pressed={handsFree}
                title="Hands-free: the guide listens after it speaks"
              >
                <FiMic size={17} />
              </IconButton>
            )}
            <IconButton type="button" onClick={close} aria-label="Close guide">
              <FiX size={19} />
            </IconButton>
          </HeaderBar>
          <Body ref={bodyRef} aria-live="polite">
            {session.messages.map((msg) => (
              <Bubble key={msg.id} $from={msg.from}>{msg.text}</Bubble>
            ))}
            {session.sending && <Bubble $from="guide">Thinking…</Bubble>}
          </Body>
          {session.options.length > 0 && (
            <Options>
              {session.options.map((choice) => (
                <Chip
                  key={choice.id}
                  type="button"
                  disabled={session.sending}
                  onClick={() => {
                    unlockAudio();
                    handle(choice.label, choice.id, choice.label);
                  }}
                >
                  {choice.label}
                </Chip>
              ))}
            </Options>
          )}
          <Composer>
            <Field
              type="text"
              placeholder={session.listening ? 'Listening…' : 'Type a page name or a question…'}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submit();
                }
              }}
              disabled={session.sending}
              aria-label="Message the guide"
            />
            {canListen && (
              <RoundButton
                type="button"
                $live={session.listening}
                onClick={() => {
                  unlockAudio();
                  if (session.listening) voice.recognizer?.stop();
                  else listen();
                }}
                aria-label={session.listening ? 'Stop listening' : 'Speak to the guide'}
              >
                <FiMic size={16} />
              </RoundButton>
            )}
            <RoundButton type="button" onClick={submit} disabled={session.sending || !input.trim()} aria-label="Send">
              <FiSend size={16} />
            </RoundButton>
          </Composer>
        </Panel>
      )}
      <Fab type="button" onClick={toggle} aria-expanded={session.open} aria-label={session.open ? 'Close guide' : 'Open guide'}>
        <FiCompass size={26} />
      </Fab>
    </Root>
  );
};

export default SiteGuide;
